import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HardDrive, Loader2, FolderOpen, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtRel } from "@/lib/api";
import { Pill, Dot } from "@/components/app/shared";

export default function CrmDriveCard({ canManage }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["crm-drive"], queryFn: async () => (await api.get("/crm/drive")).data });
  const save = useMutation({
    mutationFn: async () => (await api.put("/crm/drive", { driveId: pick })).data,
    onSuccess: (c) => { toast.success(`Unidad «${c.driveName}» lista para el CRM`); setPick(""); qc.invalidateQueries({ queryKey: ["crm-drive"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const clear = useMutation({
    mutationFn: async () => api.delete("/crm/drive"),
    onSuccess: () => { toast.success("Unidad compartida desvinculada"); qc.invalidateQueries({ queryKey: ["crm-drive"] }); },
  });

  if (isLoading || !data) return <div className="h-32 animate-pulse rounded-lg border border-border bg-card" />;
  const cfg = data.config;

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="crm-drive-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold"><HardDrive className="h-4 w-4" /> Drive del CRM · carpetas por deal</div>
          <div className="text-xs text-muted-foreground">Propuestas, firmas y actas de aceptación se archivan en: Unidad compartida / Mediana Hub · CRM / Cliente / Deal.</div>
        </div>
        <Pill tone={cfg ? (data.ownerConnected ? "mint" : "blush") : "neutral"} data-testid="crm-drive-status">
          <Dot tone={cfg ? (data.ownerConnected ? "ok" : "bad") : "muted"} />
          {cfg ? (data.ownerConnected ? `Unidad «${cfg.driveName}»` : "Requiere reconexión") : "Sin unidad elegida"}
        </Pill>
      </div>
      <div className="space-y-3 p-5">
        {cfg && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background p-3 text-[12.5px]">
            <div>
              Sube con la cuenta <span className="font-mono text-[11.5px]">{cfg.ownerEmail}</span> · configurada {fmtRel(cfg.updatedAt)}
              {!data.ownerConnected && (
                <div className="mt-1 flex items-center gap-1 text-[11.5px] text-bad"><AlertTriangle className="h-3 w-3" /> Esa cuenta desconectó Google o falta permiso de escritura. La evidencia se guarda en el Hub hasta reconectar.</div>
              )}
            </div>
            <div className="flex gap-1.5">
              {cfg.rootFolderLink && (
                <Button variant="outline" size="sm" asChild data-testid="crm-drive-open-root"><a href={cfg.rootFolderLink} target="_blank" rel="noreferrer"><FolderOpen className="mr-1 h-3.5 w-3.5" /> Abrir carpeta</a></Button>
              )}
              {canManage && <Button variant="ghost" size="sm" onClick={() => clear.mutate()} data-testid="crm-drive-clear">Desvincular</Button>}
            </div>
          </div>
        )}
        {canManage && (
          !data.myGoogleConnected ? (
            <div className="text-[12px] text-muted-foreground" data-testid="crm-drive-need-google">Conecta tu Google (arriba) para elegir la unidad compartida.</div>
          ) : !data.myDriveWrite ? (
            <div className="text-[12px] text-muted-foreground" data-testid="crm-drive-need-reconnect">Reconecta tu Google (arriba) para dar permiso de escritura en Drive.</div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger className="w-[280px]" data-testid="crm-drive-select"><SelectValue placeholder={data.sharedDrives.length ? "Elige una unidad compartida" : "No tienes unidades compartidas"} /></SelectTrigger>
                <SelectContent>
                  {data.sharedDrives.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => save.mutate()} disabled={!pick || save.isPending} data-testid="crm-drive-save">
                {save.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {cfg ? "Cambiar unidad" : "Usar esta unidad"}
              </Button>
              {data.error && <span className="text-[11.5px] text-bad">{data.error}</span>}
            </div>
          )
        )}
      </div>
    </section>
  );
}
